"use client";
import { useState, useEffect } from "react";
import { useCustomContext } from "../CustomComponents/CustomComponents";
import { insertLog } from "../Logs/InsertLog";
import DatasetDS from "../../DataServices/DatasetDs";

export default function useDatasetLogic() {
  const { showAlert, hud, stopHudRotation, showToast } = useCustomContext();
  const dsApi = new DatasetDS();

  const useremail = localStorage.getItem("email");
  const userName = useremail?.split("@")[0];

  // Dataset states
  const [allDatasets, setAllDatasets] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");

  // Modal states
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [currentDataset, setCurrentDataset] = useState(null);

  const [sortMode, setSortMode] = useState(null);


  // Fetch all datasets once
  const fetchAllDatasets = async () => {
    hud("Loading Datasets...");
    try {
      const res = await dsApi.AP_Dataset_List_GET({});
      let body = res.data.body;
      if (typeof body === "string") body = JSON.parse(body);

      if (res.data.statusCode === 200 && Array.isArray(body.records)) {


        setAllDatasets(body.records);
      } else {
        insertLog(
          `${userName} failed to fetch datasets`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          '',
          'GET',
          'AP_Dataset_List_GET',
          'FAILED',
          body?.message || "Failed to load datasets"
        );
        showAlert(body?.message || "Failed to load datasets", [
          { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
        ]);
      }
    } catch (err) {
      insertLog(
        `${userName} failed to fetch datasets`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        '',
        'GET',
        'AP_Dataset_List_GET',
        'FAILED',
        err.message
      );
      showAlert(err.message || "Error fetching datasets", [
        { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
      ]);
    } finally {
      stopHudRotation();
    }
  };

  useEffect(() => {
    fetchAllDatasets();
  }, []);

  // Reset page on search or pageSize change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, pageSize]);

  const filteredDatasets = [...allDatasets]
    .filter(ds =>
      !search || ds.datasetID?.toLowerCase().includes(search.toLowerCase())||ds.datasetName?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (!sortMode) return 0;

      const aStr = String(a.datasetID ?? "");
      const bStr = String(b.datasetID ?? "");

      return sortMode === "id-asc"
        ? aStr.localeCompare(bStr, undefined, { numeric: true })
        : bStr.localeCompare(aStr, undefined, { numeric: true });
    });

  const totalRecords = filteredDatasets.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const datasets = filteredDatasets.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleDatasetIdSort = () => {
    setSortMode(prev => {
      if (prev === null) return "id-asc";
      if (prev === "id-asc") return "id-desc";
      return "id-asc";
    });
  };

  const startEntry = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, totalRecords);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
  };
  const fetchDatasetById = async (id) => {
    if (!id) return null;

    hud("Loading Dataset...");
    try {
      const res = await dsApi.AP_Datasets_ById_GET({ id });

      let body = res.data.body;
      if (typeof body === "string") {
        body = JSON.parse(body);
      }

      if (res.data.statusCode === 200 && body.record) {
        return body.record;
      } else {
        insertLog(
          `${userName} failed to fetch dataset by id: ${id}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          id,
          'GET',
          'AP_Datasets_ById_GET',
          'FAILED',
          body?.message || "Failed to fetch dataset"
        );
        showAlert(body?.message || "Failed to fetch dataset", [
          { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
        ]);
        return null;
      }
    } catch (err) {
      insertLog(
        `${userName} failed to fetch dataset by id: ${id}`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        id,
        'GET',
        'AP_Datasets_ById_GET',
        'FAILED',
        err.message
      );
      showAlert(err.message || "Error fetching dataset", [
        { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
      ]);
      return null;
    } finally {
      stopHudRotation();
    }
  };

  const handleEditDataset = async (ds) => {
    const latestDataset = await fetchDatasetById(ds.id);
    if (latestDataset) {
      setCurrentDataset(latestDataset);
      setOpenEditModal(true);
    }
  };

  // Modal handlers
  const handleAddDataset = () => setOpenAddModal(true);
  const handleCloseAddModal = () => setOpenAddModal(false);

  const handleCloseEditModal = () => {
    setCurrentDataset(null);
    setOpenEditModal(false);
  };

  const handleSaveDataset = async (datasetDetails, isNew = false, changeNote = "") => {
    hud("Please wait...");
    handleCloseAddModal();
    handleCloseEditModal();
    try {
      if (isNew) {
        showToast({ message: "Dataset Added Successfully" });

        const fileNote = changeNote ? "with file uploaded" : "without file";

        insertLog(
          `${userName} added dataset ${datasetDetails.datasetName} (${fileNote})`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          datasetDetails.datasetID || '',
          'INSERT',
          'AP_Dataset_INSERT',
          'SUCCESS',
          ''
        );

      } else {
        showToast({ message: "Dataset Updated Successfully" });


        insertLog(
          `${userName} updated dataset ${datasetDetails.datasetName} — ${changeNote}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          datasetDetails.datasetID || '',
          'UPDATE',
          'AP_Dataset_UPDATE',
          'SUCCESS',
          ''
        );
      }

      let updatedDatasets = [...allDatasets];
      if (isNew) {
        updatedDatasets.unshift(datasetDetails);
      } else {
        updatedDatasets = updatedDatasets.map((d) =>
          d.datasetID === datasetDetails.datasetID ? datasetDetails : d
        );
      }
      setAllDatasets(updatedDatasets);
      await fetchAllDatasets();

    } catch (err) {
      insertLog(
        `${userName} failed to ${isNew ? "add" : "update"} dataset ${datasetDetails.datasetName}`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        datasetDetails.datasetID || '',
        isNew ? 'INSERT' : 'UPDATE',
        isNew ? 'AP_Dataset_INSERT' : 'AP_Dataset_UPDATE',
        'FAILED',
        err.message
      );
      showAlert(err.message || "Error saving dataset", [
        { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
      ]);
    } finally {
      stopHudRotation();
    }
  };

  return {
    datasets,
    currentPage,
    totalPages,
    handlePageChange,
    handlePageSizeChange,
    pageSize,
    totalRecords,
    startEntry,
    endEntry,
    search,
    setSearch,
    openAddModal,
    openEditModal,
    currentDataset,
    handleAddDataset,
    handleCloseAddModal,
    handleEditDataset,
    handleCloseEditModal,
    handleSaveDataset,
    handleDatasetIdSort,
    sortMode
  };
}
